<template>
  <v-app id="app">
    <div id="app-loading" class="d-flex justify-center align-center">
      <div style="text-align: center;">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <p class="mt-3 mb-5">Loading Elections</p>
      </div>
    </div>

    <template v-if="!loading">
      <forecast-header></forecast-header>
      <v-content>
        <v-container id="content-container" :fluid="$route.meta && $route.meta.fluid ? true : false">
          <div :style="!mobile ? 'display: flex; height: 100%;' : ''">
            <toolbar style="width: 180px" v-if="!mobile || showToolbar" @close="showToolbar = false"></toolbar>
            <div :style="!mobile ? 'width: calc(100% - 180px);' : 'width: 100%;'">
              <router-view :key="$route.name" :style="!mobile ? 'padding-left: 30px;' : ''" />
              <forecast-footer></forecast-footer>
            </div>
          </div>
        </v-container>
      </v-content>

      <v-btn
        v-if="mobile"
        class="mobile-search-button"
        @click="showToolbar = !showToolbar">
        <v-icon>mdi-magnify</v-icon>
        <span>Search</span>
      </v-btn>

      <disclaimer></disclaimer>
    </template>
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import ForecastHeader from '@/components/layout/Header'
import ForecastFooter from '@/components/layout/Footer'
import Toolbar from '@/components/layout/Toolbar'
import Disclaimer from '@/components/Disclaimer'
import {
  cookiesAreSupported,
  getRemainingPageViews,
} from '@/mixins/cookies'

const name = 'app'

const computed = {
  ...mapGetters({
    cds: 'geojson/cds',
    // dmasByCd: 'geojson/dmasByCd',
    // dmasByState: 'geojson/dmasByState',
    states: 'geojson/states',
    mobile: 'ux/mobile',
    isDarkMode: 'persistentUx/isDarkMode'
  })
}

const methods = {
  ...mapActions({
    getAggregateModels: 'aggregateModels/list',
    getCds: 'geojson/getCds',
    setElections: 'elections/set',
    // getDmasByCd: 'geojson/getDmasByCd',
    // getDmasByState: 'geojson/getDmasByState',
    getStates: 'geojson/getStates',
    setScreenWidth: 'ux/setWidth'
  }),
  getWindowSize () {
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout)
    }
    setTimeout(() => {
      this.setScreenWidth(document.documentElement.clientWidth)
      this.resizeTimeout = null
    }, 200)
  },
  updateCss () {
    if (this.isDarkMode) {
      this.$vuetify.theme.dark = true
    }

    if (this.isDarkMode) {
      document.body.style.background = '#121212'
      document.documentElement.style.setProperty('--background-color', '#121212')
      document.documentElement.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.12)')
      document.documentElement.style.setProperty('--secondary-color', '#222222')
      document.documentElement.style.setProperty('--text-color', '#ffffff')
    } else {
      document.body.style.background = '#FFFFFF'
      document.documentElement.style.setProperty('--background-color', '#FFFFFF')
      document.documentElement.style.setProperty('--border-color', 'rgba(0, 0, 0, 0.12)')
      document.documentElement.style.setProperty('--secondary-color', '#fafafa')
      document.documentElement.style.setProperty('--text-color', '#373737')
    }
  },
  setPageViewCookie () {
    if (!cookiesAreSupported()) return;
    getRemainingPageViews();
  },
}

const components = {
  ForecastHeader,
  ForecastFooter,
  Toolbar,
  Disclaimer
}

export default {
  name,
  components,
  computed,
  methods,
  data: () => ({
    mobileNav: null,
    loading: false,
    resizeTimeout: null,
    showToolbar: false
  }),
  beforeMount () {
    this.loading = true

    this.updateCss()

    // clear old keys when topos are updated, find better naming convention
    for (var key in localStorage) {
      if (key.includes('op_forecasting') && key !== 'op_forecasting_topojsons_v1') {
        localStorage.removeItem(key)
      }
    }

    const promises = [
      this.getAggregateModels(),
      this.setElections()
    ]

    if (this.cds.length === 0) {
      promises.push(this.getCds())
    }
    // if (this.dmasByCd.length === 0) {
    //   promises.push(this.getDmasByCd())
    // }
    // if (this.dmasByState.length === 0) {
    //   promises.push(this.getDmasByState())
    // }
    if (this.states.length === 0) {
      promises.push(this.getStates())
    }
    if (this.$auth.token) {
      // Authenticate existing token
      promises.push(this.$auth.authenticate())
    }

    Promise.all(promises).then(() => {
      this.loading = false
      if (typeof window.__PRERENDER_INJECTED === 'undefined') {
        const appLoading = document.getElementById('app-loading')
        appLoading.parentNode.removeChild(appLoading)
      }
    }).then(() => {
      if (this.$auth.authenticated === true && this.$route.name === 'login') {
        this.$router.replace('/')
      }
    })
  },
  mounted () {
    this.setPageViewCookie()
    this.getWindowSize()
    window.addEventListener('resize', this.getWindowSize)
  },
  watch: {
    $route () {
      window.scrollTo(0, 160)
    },
    '$vuetify.theme.isDark'() {
      this.updateCss()
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.getWindowSize)
  },
  metaInfo: {
    titleTemplate: (titleChunk) => {
      return titleChunk ? `${titleChunk} - Decision Desk HQ 2022 Election Predictions` : 'Decision Desk HQ 2022 Election Predictions'
    },
    meta: [
      {
        charset: 'utf-8'
      }, {
        name: 'article:publisher',
        content: 'https://www.facebook.com/DecisionDeskHQ/'
      }, {
        name: 'article:tag',
        content: 'Congress'
      }, {
        name: 'article:tag',
        content: 'Election Day'
      }, {
        name: 'article:tag',
        content: 'US House'
      }, {
        name: 'article:tag',
        content: 'US Senate'
      }, {
        name: 'og:locale',
        content: 'en_US'
      }, {
        name: 'og:type',
        content: 'website'
      }, {
        name: 'og:site_name',
        content: 'Decision Desk HQ 2022 Election Predictions'
      }, {
        vmid: 'og:image',
        name: 'og:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/open-graph.png'
      }, {
        vmid: 'og:image:secure_url',
        name: 'og:image:secure_url',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/open-graph.png'
      }, {
        name: 'og:image:width',
        content: '1200'
      }, {
        name: 'og:image:height',
        content: '600'
      }, {
        name: 'twitter:card',
        content: 'summary_large_image'
      }, {
        vmid: 'twitter:image',
        name: 'twitter:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/open-graph.png'
      }, {
        name: 'twitter:creator',
        content: '@DecisionDeskHQ'
      }, {
        vmid: 'og:title',
        name: 'og:title',
        content: 'Home'
      }, {
        vmid: 'twitter:title',
        name: 'twitter:title',
        content: 'Home'
      }, {
        vmid: 'description',
        name: 'description',
        content: '2022 U.S. election predictions for Senate and House Congressional elections. Includes model-driven predictions for key Senate and House races, polling and district information, and model simulations updated daily.'
      }, {
        vmid: 'og:description',
        name: 'og:description',
        content: '2022 U.S. election predictions for Senate and House Congressional elections. Includes model-driven predictions for key Senate and House races, polling and district information, and model simulations updated daily.'
      }, {
        vmid: 'twitter:description',
        name: 'twitter:description',
        content: '2022 U.S. election predictions for Senate and House Congressional elections. Includes model-driven predictions for key Senate and House races, polling and district information, and model simulations updated daily.'
      }
    ]
  }
}
</script>

<style>
#content-container {
  height: 100%;
  padding-bottom: 0px;
}

.v-item-group.v-bottom-navigation--horizontal .v-btn > .v-btn__content > .v-icon {
  margin-right: 0;
}

.mobile-search-button {
  border-radius: 0;
  bottom: 0;
  display: flex;
  height: 60px !important;
  position: fixed;
  width: 100vw;
  justify-content: center;
}
</style>
